import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import type {
  ConversationThread,
  InboxResponse,
  NotificationSettings,
  Reaction,
} from 'types/interfaces/message';

export const messageApi = createApi({
  reducerPath: 'messageApi',
  baseQuery: baseQuery,
  tagTypes: ['Messages', 'UnreadCount', 'NotificationSettings'],
  endpoints: (builder) => ({
    sendMessage: builder.mutation<
      { success: boolean; messageId: string; conversationId: string },
      { receiverId: number; content: string; images?: File[] }
    >({
      query: ({ receiverId, content, images }) => {
        if (images && images.length > 0) {
          const formData = new FormData();
          formData.append('receiverId', String(receiverId));
          formData.append('content', content);
          images.forEach(file => formData.append('images', file));
          return {
            url: '/messages/send',
            method: 'POST',
            body: formData,
            requiresAuth: true,
          };
        }
        return {
          url: '/messages/send',
          method: 'POST',
          body: { receiverId, content },
          requiresAuth: true,
        };
      },
      invalidatesTags: ['Messages', 'UnreadCount'],
    }),

    getInbox: builder.query<InboxResponse, { page?: number; pageSize?: number; search?: string }>({
      query: ({ page = 1, pageSize = 25, search }) => ({
        url: `/messages/inbox?page=${page}&pageSize=${pageSize}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Messages'],
    }),

    getConversationThread: builder.query<ConversationThread, string>({
      query: (conversationId) => ({
        url: `/messages/conversation/${conversationId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Messages'],
    }),

    replyToMessage: builder.mutation<
      { success: boolean; messageId: string },
      { conversationId: string; content: string; images?: File[] }
    >({
      query: ({ conversationId, content, images }) => {
        if (images && images.length > 0) {
          const formData = new FormData();
          formData.append('content', content);
          images.forEach(file => formData.append('images', file));
          return {
            url: `/messages/conversation/${conversationId}/reply`,
            method: 'POST',
            body: formData,
            requiresAuth: true,
          };
        }
        return {
          url: `/messages/conversation/${conversationId}/reply`,
          method: 'POST',
          body: { content },
          requiresAuth: true,
        };
      },
      invalidatesTags: ['Messages', 'UnreadCount'],
    }),

    markAsRead: builder.mutation<void, string>({
      query: (conversationId) => ({
        url: `/messages/conversation/${conversationId}/read`,
        method: 'PUT',
        requiresAuth: true,
      }),
      invalidatesTags: ['Messages', 'UnreadCount'],
    }),

    archiveConversation: builder.mutation<void, string>({
      query: (conversationId) => ({
        url: `/messages/conversation/${conversationId}/archive`,
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['Messages'],
    }),

    getUnreadCount: builder.query<{ count: number }, void>({
      query: () => ({
        url: '/messages/unread-count',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['UnreadCount'],
    }),

    getNotificationSettings: builder.query<NotificationSettings, void>({
      query: () => ({
        url: '/settings/notifications',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['NotificationSettings'],
    }),

    updateNotificationSettings: builder.mutation<
      { success: boolean; settings: NotificationSettings },
      { emailOnNewMessage?: boolean; emailOnNewTrade?: boolean; emailOnSystemMessage?: boolean; emailOnNewFeatures?: boolean; emailOnShipped?: boolean; soundEnabled?: boolean }
    >({
      query: (body) => ({
        url: '/settings/notifications',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['NotificationSettings'],
    }),

    addReaction: builder.mutation<
      { success: boolean },
      { messageId: string; emoji: string; conversationId: string; currentUser: { id: number; username: string } }
    >({
      query: ({ messageId, emoji }) => ({
        url: `/messages/${messageId}/reactions`,
        method: 'POST',
        body: { emoji },
        requiresAuth: true,
      }),
      async onQueryStarted({ messageId, emoji, conversationId, currentUser }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          messageApi.util.updateQueryData('getConversationThread', conversationId, (draft) => {
            const msg = draft.messages.find((m) => m.id === messageId);
            if (!msg) return;
            if (!msg.reactions) msg.reactions = [];
            const existing = msg.reactions.find((r: Reaction) => r.emoji === emoji);
            if (existing) {
              existing.count += 1;
              existing.users.push({ id: currentUser.id, username: currentUser.username });
              existing.reacted = true;
            } else {
              msg.reactions.push({
                emoji,
                count: 1,
                users: [{ id: currentUser.id, username: currentUser.username }],
                reacted: true,
              });
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    removeReaction: builder.mutation<
      { success: boolean },
      { messageId: string; emoji: string; conversationId: string; currentUserId: number }
    >({
      query: ({ messageId, emoji }) => ({
        url: `/messages/${messageId}/reactions`,
        method: 'DELETE',
        body: { emoji },
        requiresAuth: true,
      }),
      async onQueryStarted({ messageId, emoji, conversationId, currentUserId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          messageApi.util.updateQueryData('getConversationThread', conversationId, (draft) => {
            const msg = draft.messages.find((m) => m.id === messageId);
            if (!msg?.reactions) return;
            const existing = msg.reactions.find((r: Reaction) => r.emoji === emoji);
            if (!existing) return;
            existing.count -= 1;
            existing.users = existing.users.filter((u) => u.id !== currentUserId);
            existing.reacted = false;
            if (existing.count <= 0) {
              msg.reactions = msg.reactions.filter((r: Reaction) => r.emoji !== emoji);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useSendMessageMutation,
  useGetInboxQuery,
  useGetConversationThreadQuery,
  useReplyToMessageMutation,
  useMarkAsReadMutation,
  useArchiveConversationMutation,
  useGetUnreadCountQuery,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useAddReactionMutation,
  useRemoveReactionMutation,
} = messageApi;
